import { useState } from 'react';
import { AlertTriangle, ArrowLeftRight, ListFilter, Loader2 } from 'lucide-react';
import { ANALYSIS_SLOTS, AUTO_REVIEW_SLOT, canSwapSlots } from '../../../lib/analysis-models';
import { SwapAnalysisResult, useAnalysisModels, useSwapAnalysis } from '../../../queries/meetingAnalysis';
import { useMeetingRaces } from '../../../queries/meetingRaces';
import { analysisTypeLabel } from '../../../lib/history-performance';
import { useToast } from '../../../contexts/ToastContext';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

interface SlotPickerProps {
  /** Short visible label, e.g. "Swap" or "⇄". */
  label: string;
  /** Unique accessible group name for this picker, not shown visually - disambiguates
   *  identical T1-T4 buttons when several pickers are on screen at once. */
  ariaGroup: string;
  slots: readonly number[];
  value: number;
  onChange: (slot: number) => void;
  /** The slot picked on the other side: buttons that cannot swap with it are disabled. */
  pairedWith?: number;
}

function SlotPicker({ label, ariaGroup, slots, value, onChange, pairedWith }: SlotPickerProps) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="inline-flex overflow-hidden rounded-md border border-border">
        {slots.map((s) => (
          <button
            key={s}
            type="button"
            disabled={pairedWith !== undefined && !canSwapSlots(pairedWith, s)}
            title={s === AUTO_REVIEW_SLOT ? 'Auto review: swapping T5 with T1 puts the suggested tips into T1' : undefined}
            onClick={() => onChange(s)}
            aria-label={`${ariaGroup} slot T${s}`}
            className={`px-2.5 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-30 ${
              value === s ? 'bg-primary text-white' : 'bg-background hover:bg-accent'
            }`}
          >
            T{s}
          </button>
        ))}
      </div>
    </div>
  );
}

const LATEST_TIME_FMT = new Intl.DateTimeFormat('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
const latestLabel = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : LATEST_TIME_FMT.format(d);
};

/** After a "from" change, keep "to" if it can still swap, else move it to the first slot that can. */
const pairFor = (slots: readonly number[], from: number, to: number) =>
  canSwapSlots(from, to) ? to : (slots.find((s) => canSwapSlots(from, s)) ?? to);

const isAutoReviewPair = (a: number, b: number) => a === AUTO_REVIEW_SLOT || b === AUTO_REVIEW_SLOT;

function swapMessage(fromSlot: number, toSlot: number, result: SwapAnalysisResult | undefined, scope: string): string {
  if (!isAutoReviewPair(fromSlot, toSlot)) return `Swapped T${fromSlot} ⇄ T${toSlot} for ${scope}.`;
  const n = result?.appliedRaceIds?.length ?? 0;
  if (n === 0) return 'T1 already holds the auto review suggestion (races it would re-run keep T1).';
  return `Auto review applied to ${n} race${n === 1 ? '' : 's'}: T1 now holds the suggested tips.`;
}

interface AnalysisActionsBarProps {
  meetingId: string;
}

export function AnalysisActionsBar({ meetingId }: AnalysisActionsBarProps) {
  const { data: models = [] } = useAnalysisModels(meetingId);
  const { data: races = [] } = useMeetingRaces(meetingId);
  const swap = useSwapAnalysis(meetingId);
  const { success, error } = useToast();
  // T5 is offered only once the meeting has auto review suggestions
  const slots: readonly number[] = models.some((m) => Number(m.analysis_type_id) === AUTO_REVIEW_SLOT)
    ? [...ANALYSIS_SLOTS, AUTO_REVIEW_SLOT]
    : ANALYSIS_SLOTS;

  const [swapAllFromSlot, setSwapAllFromSlot] = useState(1);
  const [swapAllToSlot, setSwapAllToSlot] = useState(2);

  const [showRacePicker, setShowRacePicker] = useState(false);
  const [selectedRaceIds, setSelectedRaceIds] = useState<Set<string>>(new Set());
  const [raceFromSlot, setRaceFromSlot] = useState(1);
  const [raceToSlot, setRaceToSlot] = useState(2);

  const toggleRace = (raceId: string) =>
    setSelectedRaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(raceId)) next.delete(raceId);
      else next.add(raceId);
      return next;
    });

  const handleSwapAll = () => {
    swap.mutate(
      { fromSlot: swapAllFromSlot, toSlot: swapAllToSlot },
      {
        onSuccess: (result) => success(swapMessage(swapAllFromSlot, swapAllToSlot, result, 'all races')),
        onError: () => error('Failed to swap analysis slots. Please try again.'),
      }
    );
  };

  const handleConfirmSwap = () => {
    if (selectedRaceIds.size === 0) return;
    const count = selectedRaceIds.size;
    swap.mutate(
      { raceIds: Array.from(selectedRaceIds), fromSlot: raceFromSlot, toSlot: raceToSlot },
      {
        onSuccess: (result) => {
          success(swapMessage(raceFromSlot, raceToSlot, result, `${count} race${count === 1 ? '' : 's'}`));
          setShowRacePicker(false);
          setSelectedRaceIds(new Set());
        },
        onError: () => error('Failed to swap analysis slots. Please try again.'),
      }
    );
  };

  return (
    <Card className="p-4">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold">Analysis Models</h3>
        {models.length > 0 ? (
          models.map((m) => {
            const hasCount = m.race_count != null && m.total_races != null;
            const complete = hasCount && (m.race_count ?? 0) >= (m.total_races ?? 0);
            const latest = latestLabel(m.latest_analysis_at);
            return (
              <span key={m.analysis_type_id} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <span>{analysisTypeLabel(m.analysis_type_id)}:</span>
                <span className="font-medium text-foreground">{m.ai_model ?? '—'}</span>
                {m.ai_version && <span className="font-mono text-xs">{m.ai_version}</span>}
                {hasCount && (
                  <span className={`font-semibold ${complete ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {m.race_count}/{m.total_races}
                  </span>
                )}
                {!!m.scratched_tips && (
                  <span
                    className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
                    title={`${m.scratched_tips} scratched pick${m.scratched_tips === 1 ? '' : 's'} in this slot`}
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {m.scratched_tips}
                  </span>
                )}
                {latest && <span className="text-xs">· {latest}</span>}
              </span>
            );
          })
        ) : (
          <span className="text-sm italic text-muted-foreground">No analysis data</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SlotPicker
          label="Swap"
          ariaGroup="Swap all races: from"
          slots={slots}
          value={swapAllFromSlot}
          onChange={(s) => {
            setSwapAllFromSlot(s);
            setSwapAllToSlot(pairFor(slots, s, swapAllToSlot));
          }}
        />
        <SlotPicker
          label="⇄"
          ariaGroup="Swap all races: to"
          slots={slots}
          value={swapAllToSlot}
          onChange={setSwapAllToSlot}
          pairedWith={swapAllFromSlot}
        />
        <Button onClick={handleSwapAll} disabled={swap.isPending} size="sm">
          {swap.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowLeftRight className="h-3.5 w-3.5" />}
          Swap All Races
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowRacePicker((v) => !v)} disabled={swap.isPending}>
          <ListFilter className="h-3.5 w-3.5" />
          {showRacePicker ? 'Hide Races' : 'Select Races to Swap'}
        </Button>
      </div>

      {showRacePicker && races.length > 0 && (
        <div className="mt-3">
          <div className="mb-3 flex items-center gap-3">
            <SlotPicker
              label="Swap"
              ariaGroup="Swap selected races: from"
              slots={slots}
              value={raceFromSlot}
              onChange={(s) => {
                setRaceFromSlot(s);
                setRaceToSlot(pairFor(slots, s, raceToSlot));
              }}
            />
            <SlotPicker
              label="⇄"
              ariaGroup="Swap selected races: to"
              slots={slots}
              value={raceToSlot}
              onChange={setRaceToSlot}
              pairedWith={raceFromSlot}
            />
          </div>
          <p className="mb-2 text-xs text-muted-foreground">Select races to swap:</p>
          <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {races.map((race) => (
              <label key={race.race_id} className="flex items-center gap-1.5 text-sm">
                <Checkbox checked={selectedRaceIds.has(race.race_id)} onChange={() => toggleRace(race.race_id)} />
                R{race.race_number}
                {race.race_name ? ` – ${race.race_name}` : ''}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleConfirmSwap} disabled={swap.isPending || selectedRaceIds.size === 0} size="sm">
              {swap.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowLeftRight className="h-3.5 w-3.5" />}
              Swap T{raceFromSlot} ⇄ T{raceToSlot} ({selectedRaceIds.size})
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowRacePicker(false);
                setSelectedRaceIds(new Set());
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
